import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from './services/authService';
import {
  ReactFlow,
  Node as FlowNode,
  Edge as FlowEdge,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  MarkerType,
  OnConnect,
  ReactFlowProvider,
  useReactFlow,
  SelectionMode
} from '@reactflow/core';
import '@reactflow/core/dist/style.css';
import { Background, BackgroundVariant } from '@reactflow/background';
import { MiniMap } from '@reactflow/minimap';

import { Download, Upload, FolderOpen, Eye, EyeOff, Plus, Minus, User, Save, Undo, Redo, CheckCircle, AlertCircle, Loader2, MoreHorizontal, Settings, Share, Maximize2, Shield } from 'lucide-react';
import logo from './assets/logo.jpg';
// Undo/Redo integrated into Toolbar
import toast from 'react-hot-toast';
import CustomNode from './components/CustomNode';
import Toolbar from './components/Toolbar';
import Sidebar from './components/Sidebar';
import ContextMenu from './components/ContextMenu';
import BoardLibrary from './components/BoardLibrary';
import Timeline from './components/Timeline';
import BoardThemeSwitch from './components/BoardThemeSwitch';
import AdminLayout from './components/admin/AdminLayout';
import UserManagement from './components/admin/UserManagement';
import AdminMessaging from './components/admin/AdminMessaging';
import AdminAuditLog from './components/admin/AdminAuditLog';
import RecoveryUtility from './components/RecoveryUtility';
import { Board, Node as BoardNode, Edge as BoardEdge, NODE_TYPES } from './types';
import { BoardStorage } from './utils/storage';
import { exportBoardAsCSV, exportCanvasAsJPG, importBoardFromJSON, exportBoardAsPortableHTML, exportBoardAsJSON } from './utils/export';
import { applyDagreLayout } from './utils/layout';
import { createDemoBoard } from './data/demoData';
import { useTheme } from './hooks/useTheme';
import { useSearchParams } from 'react-router-dom';
import { BoardProvider } from './context/BoardContext';

const nodeTypes = {
  custom: CustomNode,
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const boardIdFromUrl = searchParams.get('boardId');

  // State management
  const [user, setUser] = useState(authService.getCurrentUser());
  const [currentBoard, setCurrentBoard] = useState<Board | null>(null);
  const { theme, toggleTheme } = useTheme();
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const isInitializedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Subscribe to auth changes to reflect role/promotions immediately
  useEffect(() => {
    const unsubscribe = authService.subscribe(() => {
      const currentUser = authService.getCurrentUser();
      console.log('Fibre: Auth changed, current user:', currentUser?.email, 'role:', currentUser?.role);
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Consolidated board initialization — single effect eliminates race condition
  useEffect(() => {
    let isMounted = true;
    const initBoard = async () => {
      isInitializedRef.current = false;
      let board: Board | null = null;

      try {
        if (boardIdFromUrl) {
          board = await BoardStorage.getInstance().getBoard(boardIdFromUrl, authService.getCurrentUser()?.id || null);
        }

        if (!board) {
          const boards = await BoardStorage.getInstance().getAllBoards(authService.getCurrentUser()?.id || null);
          if (boards.length > 0) {
            board = boards[0];
          }
        }

        if (!board) {
          board = createDemoBoard();
          await BoardStorage.getInstance().saveBoard(board, authService.getCurrentUser()?.id || null);
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
        board = createDemoBoard();
      }

      if (board && isMounted) {
        // Recovery logic: if board is empty OR local version is newer
        const localBackupKey = `fibre_backup_${board.id}`;
        const backupStr = localStorage.getItem(localBackupKey);

        if (backupStr) {
          try {
            const backup = JSON.parse(backupStr) as Board;
            const cloudTimestamp = board.updatedAt || 0;
            const localTimestamp = backup.updatedAt || 0;

            // Trigger recovery if local data exists AND (cloud is empty OR local is newer)
            const isLocalNewer = localTimestamp > cloudTimestamp + 1000; // 1s buffer
            const isCloudEmpty = board.nodes.length === 0 && backup.nodes.length > 0;

            if (isCloudEmpty || isLocalNewer) {
              toast((t) => (
                <div className="p-4 bg-blue-600 text-white rounded-lg shadow-2xl border-2 border-white flex flex-col gap-3 max-w-sm">
                  <div className="flex items-center gap-2 font-black uppercase italic">
                    <Shield size={20} />
                    {isCloudEmpty ? 'Emergency Backup Found' : 'Newer Offline Version Found'}
                  </div>
                  <p className="text-xs font-medium">
                    We found a {isLocalNewer ? 'newer' : ''} backup from {new Date(localTimestamp).toLocaleString()}.
                    {isLocalNewer ? ' It matches your last edits before refresh.' : ' It will restore your missing data.'}
                  </p>
                  <div className="flex gap-2 justify-end mt-1">
                    <button
                      onClick={() => {
                        loadBoard(backup);
                        toast.dismiss(t.id);
                        toast.success('DATA RESTORED SUCCESSFULLY');
                      }}
                      className="px-4 py-2 bg-white text-blue-600 rounded font-black text-xs uppercase hover:bg-gray-100 transition-all font-mono"
                    >
                      Restore Now
                    </button>
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="px-3 py-2 bg-blue-700 text-white rounded text-xs font-bold hover:bg-blue-800"
                    >
                      Keep Cloud
                    </button>
                  </div>
                </div>
              ), { duration: Infinity, position: 'top-center' });
            }
          } catch (e) {
            console.error('Failed to parse backup', e);
          }
        }

        setCurrentBoard(board);
        setNodes(board.nodes.map(n => ({ ...n, type: 'custom', data: n })));
        setEdges(board.edges.map(e => ({ ...e, type: 'default' })));
      }

      // Allow auto-save only after init is fully complete
      setTimeout(() => {
        if (isMounted) isInitializedRef.current = true;
      }, 2000);
    };
    initBoard();
    return () => { isMounted = false; };
  }, [boardIdFromUrl, setNodes, setEdges]);

  const [selectedTool, setSelectedTool] = useState('select');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [connectionStart, setConnectionStart] = useState<string | null>(null);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasBackups, setHasBackups] = useState(false);

  useEffect(() => {
    // Check for backups periodically or on mount
    const checkBackups = () => {
      for (let i = 0; i < localStorage.length; i++) {
        if (localStorage.key(i)?.startsWith('fibre_backup_')) {
          setHasBackups(true);
          return;
        }
      }
      setHasBackups(false);
    };
    checkBackups();
    const interval = setInterval(checkBackups, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-open sidebar on selection
  useEffect(() => {
    if (selectedNodeId || selectedEdgeId) {
      setShowSidebar(true);
    }
  }, [selectedNodeId, selectedEdgeId]);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    canvasPosition: { x: number; y: number };
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const storage = BoardStorage.getInstance();
  const reactFlowInstance = useReactFlow();

  const [history, setHistory] = useState<{ nodes: FlowNode[]; edges: FlowEdge[] }[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyStateRef = useRef({ history: [] as { nodes: FlowNode[]; edges: FlowEdge[] }[], index: -1 });

  // Keep ref synced for initial loads
  useEffect(() => {
    historyStateRef.current = { history, index: historyIndex };
  }, [history, historyIndex]);

  // Save state to history reliably
  const saveToHistory = useCallback(() => {
    const currentNodes = reactFlowInstance ? reactFlowInstance.getNodes() : nodes;
    const currentEdges = reactFlowInstance ? reactFlowInstance.getEdges() : edges;

    // Deep clone to prevent React Flow from mutating saved history states
    const currentState = {
      nodes: currentNodes.map(n => ({ ...n, position: { ...n.position }, data: { ...(n.data || {}) } })),
      edges: currentEdges.map(e => ({ ...e, style: { ...(e.style || {}) }, data: { ...(e.data || {}) } }))
    };

    const prevHistory = historyStateRef.current.history;
    const prevIndex = historyStateRef.current.index;

    const newHistory = prevHistory.slice(0, prevIndex + 1);
    newHistory.push(currentState);

    if (newHistory.length > 11) {
      newHistory.shift();
    }

    const newIndex = newHistory.length - 1;

    // Update ref immediately to prevent race conditions from queued timeouts
    historyStateRef.current = { history: newHistory, index: newIndex };
    setHistory(newHistory);
    setHistoryIndex(newIndex);
  }, [nodes, edges, reactFlowInstance]);

  // Centralized save function triggered by events
  const triggerSaveBoard = useCallback(async (immediate = false) => {
    if (!isInitializedRef.current || !currentBoard) return;

    const currentNodes = reactFlowInstance ? reactFlowInstance.getNodes() : nodes;
    const currentEdges = reactFlowInstance ? reactFlowInstance.getEdges() : edges;

    // CRITICAL SAFETY GUARD: Don't auto-save if board suddenly has 0 nodes/edges but previously had some
    if (!immediate && currentNodes.length === 0 && currentBoard.nodes.length > 0) {
      console.warn("Save blocked: Attempting to save empty board nodes while initialized.");
      return;
    }

    if (!immediate && currentEdges.length === 0 && currentBoard.edges.length > 0) {
      console.warn("Save blocked: Attempting to save empty board edges while initialized.");
      return;
    }

    setSaveStatus('saving');

    const performSave = async () => {
      try {
        const updatedBoard: Board = {
          ...currentBoard,
          nodes: currentNodes.map(n => {
            // Robust dimension capturing: Prefer n.width/height, fallback to style, then to data
            const width = n.width || (typeof n.style?.width === 'number' ? n.style.width : parseInt(String(n.style?.width || 0))) || n.data?.width;
            const height = n.height || (typeof n.style?.height === 'number' ? n.style.height : parseInt(String(n.style?.height || 0))) || n.data?.height;

            return {
              ...n.data,
              position: n.position,
              width: width || undefined,
              height: height || undefined
            };
          }),
          edges: currentEdges.map(e => ({
            id: e.id,
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle,
            targetHandle: e.targetHandle,
            label: (e.label as string) || '',
            color: (e.style?.stroke as string) || '#6B7280',
            ts: e.data?.ts || null,
            notesMd: e.data?.notesMd || ''
          })),
          updatedAt: Date.now()
        };

        const storage = BoardStorage.getInstance();
        await storage.saveBoard(updatedBoard, authService.getCurrentUser()?.id || null, immediate);
        setCurrentBoard(updatedBoard);
        setSaveStatus('saved');
        // Clear status after 2 seconds
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (error) {
        console.error('Save failed:', error);
        setSaveStatus('error');
      }
    };

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    if (immediate) {
      setSaveStatus('saving');
      await performSave();
    } else {
      // Background auto-save doesn't show "Saving..." to keep UI clean
      saveTimerRef.current = setTimeout(performSave, 5000); // Shortened to 5s for better resilience
    }
  }, [nodes, edges, currentBoard, reactFlowInstance]);

  // Undo functionality
  const handleUndo = useCallback(() => {
    const { history: currHistory, index } = historyStateRef.current;
    if (index > 0) {
      const prevIndex = index - 1;
      const previousState = currHistory[prevIndex];

      // Deep clone restore
      setNodes(previousState.nodes.map(n => ({ ...n, position: { ...n.position }, data: { ...(n.data || {}) } })));
      setEdges(previousState.edges.map(e => ({ ...e, style: { ...(e.style || {}) }, data: { ...(e.data || {}) } })));

      historyStateRef.current.index = prevIndex;
      setHistoryIndex(prevIndex);

      // Save to backend momentarily after React Flow updates internal state
      setTimeout(() => triggerSaveBoard(true), 50);
    }
  }, [setNodes, setEdges, triggerSaveBoard]);

  // Redo functionality
  const handleRedo = useCallback(() => {
    const { history: currHistory, index } = historyStateRef.current;
    if (index < currHistory.length - 1) {
      const nextIndex = index + 1;
      const nextState = currHistory[nextIndex];

      // Deep clone restore
      setNodes(nextState.nodes.map(n => ({ ...n, position: { ...n.position }, data: { ...(n.data || {}) } })));
      setEdges(nextState.edges.map(e => ({ ...e, style: { ...(e.style || {}) }, data: { ...(e.data || {}) } })));

      historyStateRef.current.index = nextIndex;
      setHistoryIndex(nextIndex);

      // Save to backend momentarily after React Flow updates internal state
      setTimeout(() => triggerSaveBoard(true), 50);
    }
  }, [setNodes, setEdges, triggerSaveBoard]);

  // Emergency save on tab close / navigation away
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentBoard && isInitializedRef.current && nodes.length > 0) {
        const updatedBoard: Board = {
          ...currentBoard,
          nodes: nodes.map(n => ({
            ...n.data,
            position: n.position,
            width: n.width,
            height: n.height
          })),
          edges: edges.map(e => ({
            id: e.id,
            source: e.source,
            target: e.target,
            label: e.label as string,
            color: e.style?.stroke as string,
            ts: e.data?.ts,
            notesMd: e.data?.notesMd
          }))
        };
        // Use synchronous localStorage as a last-resort backup
        try {
          localStorage.setItem(`fibre_backup_${currentBoard.id}`, JSON.stringify(updatedBoard));
        } catch (err) {
          // Best effort
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentBoard, nodes, edges]);


  const loadBoard = (board: Board) => {
    // SESSION START BACKUP: Redundant local save when opening a board
    try {
      localStorage.setItem(`fibre_session_start_${board.id}`, JSON.stringify(board));
    } catch (e) {
      console.warn("Failed to create session start backup", e);
    }

    // Pause auto-save during board switch to prevent stale data overwriting
    isInitializedRef.current = false;
    navigate(`/app?boardId=${board.id}`, { replace: true });
    setCurrentBoard(board);

    const flowNodes: FlowNode[] = board.nodes.map(node => ({
      id: node.id,
      type: 'custom',
      position: node.position,
      width: node.width,
      height: node.height,
      style: {
        width: node.width,
        height: node.height
      },
      data: node
    }));

    const flowEdges: FlowEdge[] = board.edges.map(edge => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: 'default',
      data: { ts: edge.ts, notesMd: edge.notesMd },
      label: edge.label,
      style: { stroke: edge.color || '#6B7280' },
      markerEnd: { type: MarkerType.ArrowClosed, color: edge.color || '#6B7280' }
    }));

    setNodes(flowNodes);
    setEdges(flowEdges);

    // Reset history for new board
    setHistory([{ nodes: flowNodes, edges: flowEdges }]);
    setHistoryIndex(0);

    // Re-enable auto-save after short delay
    setTimeout(() => {
      isInitializedRef.current = true;
    }, 1000);
  };

  const handleNodeClick = (event: React.MouseEvent, node: FlowNode) => {
    // If strict connect mode is on, we can still use click-to-connect
    if (selectedTool === 'connect') {
      if (!connectionStart) {
        // Start connection
        setConnectionStart(node.id);
        setSelectedNodeId(node.id);
        setSelectedEdgeId(null);
      } else if (connectionStart !== node.id) {
        // Complete connection
        const newEdge: FlowEdge = {
          id: `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          source: connectionStart as string,
          target: node.id,
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#6B7280' },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#6B7280',
          },
        };
        setEdges(eds => addEdge(newEdge, eds));
        setConnectionStart(null);
        setSelectedNodeId(null);

        // Save to history after adding edge
        setTimeout(saveToHistory, 0);
      }
    } else {
      // Normal selection
      setSelectedNodeId(node.id);
      setSelectedEdgeId(null);
      setConnectionStart(null);
    }
  };

  const handleEdgeClick = (event: React.MouseEvent, edge: FlowEdge) => {
    event.stopPropagation(); // Prevent canvas click
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null);
    setConnectionStart(null);
  };

  const handlePaneClick = () => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setConnectionStart(null);
    setContextMenu(null);
  };

  const handlePaneContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();

    // Get canvas position for node placement
    const reactFlowBounds = event.currentTarget.getBoundingClientRect();
    const canvasPosition = reactFlowInstance.screenToFlowPosition({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top,
    });

    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      canvasPosition
    });
  };

  const addNode = (type: keyof typeof NODE_TYPES) => {
    const newNode: BoardNode = {
      id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      label: `New ${NODE_TYPES[type].label}`,
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
      color: NODE_TYPES[type].color,
      tags: [],
      notesMd: '',
      attachments: []
    };

    const flowNode: FlowNode = {
      id: newNode.id,
      type: 'custom',
      position: newNode.position,
      data: newNode
    };

    setNodes(nds => [...nds, flowNode]);

    // Save to history and trigger immediate save for structure changes
    setTimeout(saveToHistory, 0);
    triggerSaveBoard(true);
  };

  const addNodeAtPosition = (type: keyof typeof NODE_TYPES, position: { x: number; y: number }) => {
    const newNode: BoardNode = {
      id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      label: `New ${NODE_TYPES[type].label}`,
      position,
      color: NODE_TYPES[type].color,
      tags: [],
      notesMd: '',
      attachments: []
    };

    const flowNode: FlowNode = {
      id: newNode.id,
      type: 'custom',
      position: newNode.position,
      data: newNode
    };

    setNodes(nds => [...nds, flowNode]);

    // Save to history and trigger immediate save for structure changes
    setTimeout(saveToHistory, 0);
    triggerSaveBoard(true);
  };

  const addStickyNote = () => {
    const newNode: BoardNode = {
      id: `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'generic',
      label: 'Sticky Note',
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
      color: '#FCD34D',
      tags: ['note'],
      notesMd: '',
      attachments: [],
      customEntityType: 'Note'
    };

    const flowNode: FlowNode = {
      id: newNode.id,
      type: 'custom',
      position: newNode.position,
      data: newNode,
      style: { width: 200, height: 120 }
    };

    setNodes(nds => [...nds, flowNode]);
    setTimeout(saveToHistory, 0);
    triggerSaveBoard(true);
  };

  const addGroup = () => {
    const newNode: BoardNode = {
      id: `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'generic',
      label: 'Group',
      position: { x: Math.random() * 400 + 50, y: Math.random() * 300 + 50 },
      color: '#818CF8',
      tags: ['group'],
      notesMd: '',
      attachments: [],
      customEntityType: 'Group'
    };

    const flowNode: FlowNode = {
      id: newNode.id,
      type: 'custom',
      position: newNode.position,
      data: newNode,
      style: { width: 400, height: 300 }
    };

    setNodes(nds => [...nds, flowNode]);
    setTimeout(saveToHistory, 0);
    triggerSaveBoard(true);
  };

  const onConnect: OnConnect = useCallback((connection: Connection) => {
    const newEdge: FlowEdge = {
      ...connection,
      id: `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      source: connection.source as string,
      target: connection.target as string,
      type: 'default', // Using 'default' (bezier) for flexibility as requested
      animated: false,
      style: { stroke: '#6B7280' },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#6B7280',
      },
      data: { ts: Date.now(), notesMd: '' }
    };
    setEdges(eds => addEdge(newEdge, eds));

    // Immediate save for connectivity changes
    setTimeout(() => {
      saveToHistory();
      triggerSaveBoard(true);
    }, 0);
  }, [setEdges, saveToHistory, triggerSaveBoard]);

  const updateNode = (updatedNode: BoardNode) => {
    setNodes(nds => nds.map(n =>
      n.id === updatedNode.id ? {
        ...n,
        data: updatedNode,
        position: n.position,
        width: updatedNode.width,
        height: updatedNode.height,
        style: {
          ...n.style,
          width: updatedNode.width,
          height: updatedNode.height
        }
      } : n
    ));
    triggerSaveBoard(true);
  };

  const handleSelectNodeFromTimeline = (nodeId: string) => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        selected: node.id === nodeId,
      }))
    );
    // Center view on the node
    const node = nodes.find(n => n.id === nodeId);
    if (node && reactFlowInstance) {
      reactFlowInstance.setCenter(node.position.x, node.position.y, { zoom: 1.2, duration: 800 });
    }
  };

  const updateEdge = (updatedEdge: BoardEdge) => {
    setEdges(eds => eds.map(e =>
      e.id === updatedEdge.id
        ? {
          ...e,
          label: updatedEdge.label,
          style: { stroke: updatedEdge.color || '#6B7280' },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: updatedEdge.color || '#6B7280',
          },
          data: { ts: updatedEdge.ts, notesMd: updatedEdge.notesMd }
        }
        : e
    ));
    triggerSaveBoard(true);

    // Force re-render by updating the selected edge reference
    if (selectedEdgeId === updatedEdge.id) {
      // The selectedEdge will be updated through the edges state change
    }
  };

  const deleteSelected = () => {
    if (selectedNodeId || selectedEdgeId) {
      const type = selectedNodeId ? 'entity' : 'connection';
      if (!window.confirm(`Are you sure you want to delete this ${type}?`)) {
        return;
      }
    }

    if (selectedNodeId) {
      setNodes(nds => nds.filter(n => n.id !== selectedNodeId));
      setEdges(eds => eds.filter(e => e.source !== selectedNodeId && e.target !== selectedNodeId));
      setSelectedNodeId(null);
      setTimeout(saveToHistory, 0);
    }
    if (selectedEdgeId) {
      setEdges(eds => eds.filter(e => e.id !== selectedEdgeId));
      setSelectedEdgeId(null);
      setTimeout(saveToHistory, 0);
    }
    triggerSaveBoard(true);
  };

  const duplicateSelected = () => {
    if (selectedNodeId) {
      const node = nodes.find(n => n.id === selectedNodeId);
      if (node) {
        const newNode: BoardNode = {
          ...node.data,
          id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          label: `${node.data.label} (Copy)`,
          position: { x: node.position.x + 50, y: node.position.y + 50 }
        };

        const flowNode: FlowNode = {
          id: newNode.id,
          type: 'custom',
          position: newNode.position,
          data: newNode
        };

        setNodes(nds => [...nds, flowNode]);
        setTimeout(saveToHistory, 0);
        triggerSaveBoard(true);
      }
    }
  };

  const handleZoomIn = () => {
    reactFlowInstance.zoomIn();
  };

  const handleZoomOut = () => {
    reactFlowInstance.zoomOut();
  };

  const handleFitView = () => {
    reactFlowInstance.fitView({ padding: 0.2, duration: 800 });
  };

  const applyAutoLayout = () => {
    const boardNodes = nodes.map(n => n.data);
    const boardEdges = edges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label as string,
      color: e.style?.stroke as string,
      ts: e.data?.ts,
      notesMd: e.data?.notesMd
    }));

    const layoutedNodes = applyDagreLayout(boardNodes, boardEdges);

    setNodes(layoutedNodes.map(node => ({
      id: node.id,
      type: 'custom',
      position: node.position,
      data: node
    })));

    // Save to history after layout
    setTimeout(saveToHistory, 0);
    triggerSaveBoard(true);
  };

  const createNewBoard = async () => {
    const newBoard: Board = {
      id: `board_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: 'New Investigation',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      nodes: [],
      edges: []
    };

    try {
      await storage.saveBoard(newBoard);
      loadBoard(newBoard);
    } catch (error) {
      console.error('Failed to create new board:', error);
    }
  };

  // Build a fresh board snapshot from live React state
  const getLiveBoardSnapshot = (): Board | null => {
    if (!currentBoard) return null;

    // Use ReactFlow instance for immediate state if available to avoid React state batching lag
    // This is critical for capturing updates (like image uploads) triggers immediately before save
    const currentNodes = reactFlowInstance ? reactFlowInstance.getNodes() : nodes;
    const currentEdges = reactFlowInstance ? reactFlowInstance.getEdges() : edges;

    return {
      ...currentBoard,
      nodes: currentNodes.map((n: any) => ({
        ...n.data,
        position: n.position,
        width: n.width,
        height: n.height
      })),
      edges: currentEdges.map((e: any) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        label: (e.label as string) || '',
        color: (e.style?.stroke as string) || '#6B7280',
        ts: e.data?.ts,
        notesMd: e.data?.notesMd || ''
      })),
      updatedAt: Date.now()
    };
  };

  const exportCSV = () => {
    const board = getLiveBoardSnapshot();
    if (board) {
      exportBoardAsCSV(board, authService.getCurrentUser());
    }
  };

  const exportJPG = async () => {
    if (currentBoard) {
      // Store current viewport
      const currentViewport = reactFlowInstance.getViewport();

      try {
        // Fit view to show everything with extra padding to avoid clipping
        reactFlowInstance.fitView({ padding: 0.5, duration: 0 });

        // Temporarily remove truncation on node labels so full names appear in export
        const wrapper = document.getElementById('reactflow-wrapper');
        if (wrapper) {
          // Remove truncate class and expand max-width on all nodes for capture
          const truncatedEls = wrapper.querySelectorAll('.truncate');
          truncatedEls.forEach(el => {
            el.classList.remove('truncate');
            el.classList.add('__export-no-truncate');
          });
          const constrainedNodes = wrapper.querySelectorAll('.max-w-\\[250px\\]');
          constrainedNodes.forEach(el => {
            (el as HTMLElement).style.maxWidth = 'none';
            el.classList.add('__export-expanded');
          });

          // Wait for render to update (generous delay)
          await new Promise(resolve => setTimeout(resolve, 500));

          await exportCanvasAsJPG('reactflow-wrapper', `${currentBoard.title}_${Date.now()}.jpg`);

          // Restore truncation and max-width
          wrapper.querySelectorAll('.__export-no-truncate').forEach(el => {
            el.classList.add('truncate');
            el.classList.remove('__export-no-truncate');
          });
          wrapper.querySelectorAll('.__export-expanded').forEach(el => {
            (el as HTMLElement).style.maxWidth = '';
            el.classList.remove('__export-expanded');
          });
        }

        toast.success('Board exported to JPG');
      } catch (error) {
        console.error('Failed to export JPG:', error);
        toast.error('Failed to export image');
      } finally {
        // Restore viewport
        reactFlowInstance.setViewport(currentViewport);
      }
    }
  };

  const exportPortableHTML = () => {
    const board = getLiveBoardSnapshot();
    if (board) {
      exportBoardAsPortableHTML(board, authService.getCurrentUser());
    }
  };

  const exportJSON = () => {
    const board = getLiveBoardSnapshot();
    if (board) {
      exportBoardAsJSON(board);
    }
  };

  const importJSON = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importedBoard = importBoardFromJSON(text);
      await storage.saveBoard(importedBoard);
      loadBoard(importedBoard);

      // Clear the input
      event.target.value = '';
    } catch (error) {
      console.error('Import failed:', error);
      alert('Failed to import file. Please check that it\'s a valid JSON board file.');
    }
  };

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Ignore if typing in input fields
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    if (event.key === 'Escape') {
      setConnectionStart(null);
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      deleteSelected();
    } else if (event.key >= '1' && event.key <= '5') {
      const typeMap = ['person', 'vehicle', 'location', 'phone', 'generic'];
      const type = typeMap[parseInt(event.key) - 1] as keyof typeof NODE_TYPES;
      addNode(type);
    } else if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      // Auto-save is already handled
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
      event.preventDefault();
      duplicateSelected();
    } else if (event.key === 's' || event.key === 'S') {
      setSelectedTool('select');
      setConnectionStart(null);
    } else if (event.key === 'p' || event.key === 'P') {
      setSelectedTool('pan');
      setConnectionStart(null);
    } else if (event.key === 'c' || event.key === 'C') {
      setSelectedTool('connect');
      setConnectionStart(null);
    } else if (event.key === 'g' || event.key === 'G') {
      setSnapToGrid(!snapToGrid);
    } else if (event.key === 'f' || event.key === 'F') {
      handleFitView();
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
      event.preventDefault();
      handleUndo();
    } else if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
      event.preventDefault();
      handleRedo();
    }
  }, [selectedNodeId, selectedEdgeId, selectedTool, snapToGrid, duplicateSelected, deleteSelected, handleUndo, handleRedo, handleFitView]);

  const handleNodeDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleNodeDragStop = useCallback((event: any, node: any) => {
    setIsDragging(false);

    if (snapToGrid) {
      const gridSize = 20;
      const snappedX = Math.round(node.position.x / gridSize) * gridSize;
      const snappedY = Math.round(node.position.y / gridSize) * gridSize;

      // Update the node position to snapped coordinates
      setNodes(nds => nds.map(n =>
        n.id === node.id
          ? { ...n, position: { x: snappedX, y: snappedY } }
          : n
      ));
    }

    // Save to history and trigger save
    setTimeout(saveToHistory, 0);
    triggerSaveBoard(true);
  }, [snapToGrid, setNodes, saveToHistory, triggerSaveBoard]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const selectedNode = selectedNodeId ? nodes.find(n => n.id === selectedNodeId)?.data || null : null;
  const selectedEdge = selectedEdgeId ? edges.find(e => e.id === selectedEdgeId) : null;
  const selectedBoardEdge = selectedEdge ? {
    id: selectedEdge.id,
    source: selectedEdge.source,
    target: selectedEdge.target,
    label: selectedEdge.label as string,
    color: selectedEdge.style?.stroke as string,
    ts: selectedEdge.data?.ts,
    notesMd: selectedEdge.data?.notesMd
  } : null;

  return (
    <BoardProvider value={{ triggerSaveBoard }}>
      <div
        className="
        h-screen flex flex-col transition-colors duration-200
        data-[theme=light]:bg-slate-100 
        data-[theme=dark]:bg-slate-900
      "
        data-theme={theme}
      >
        {/* Top Bar */}
        <div className="
        px-3 py-1.5 flex items-center justify-between border-b transition-colors duration-200
        data-[theme=light]:bg-white data-[theme=light]:border-slate-200
        data-[theme=dark]:bg-slate-900/90 data-[theme=dark]:border-slate-800
      ">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <h1 className="
              text-lg font-bold flex items-center gap-2 transition-colors duration-200
              text-slate-900 dark:text-white
            ">
                <img src={logo} alt="Fibre" className="h-5 w-auto" />
                Fibre
              </h1>
            </Link>
            {currentBoard && currentBoard.title !== 'Mock Title' && currentBoard.title !== 'Investigation Demo' && (
              <span className="
              text-xs transition-colors duration-200
              text-slate-500 dark:text-slate-400
            ">
                {currentBoard.title}
              </span>
            )}
            {/* Save Status Indicator */}
            {saveStatus === 'saving' && (
              <span className="flex items-center gap-1 text-xs text-amber-500 animate-slow-pulse">
                <Loader2 size={12} className="animate-spin" />
                Saving…
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1 text-xs text-emerald-500">
                <CheckCircle size={12} />
                Saved
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
                <AlertCircle size={12} />
                Save failed
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <BoardThemeSwitch theme={theme} onToggle={toggleTheme} />

            {/* Library Button - Always visible but label hidden on very small screens */}
            <button
              onClick={() => setShowLibrary(true)}
              className="
              flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors duration-200
              data-[theme=light]:bg-slate-100 data-[theme=light]:text-slate-700 data-[theme=light]:hover:bg-slate-200
              data-[theme=dark]:bg-slate-800 data-[theme=dark]:text-slate-200 data-[theme=dark]:hover:bg-slate-700
            "
              data-theme={theme}
              title="Library"
            >
              <FolderOpen size={14} />
              <span className="hidden sm:inline">Library</span>
            </button>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-1.5">
              <button
                onClick={exportCSV}
                className="
                flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors duration-200
                data-[theme=light]:bg-slate-100 data-[theme=light]:text-slate-700 data-[theme=light]:hover:bg-slate-200
                data-[theme=dark]:bg-slate-800 data-[theme=dark]:text-slate-200 data-[theme=dark]:hover:bg-slate-700
              "
                data-theme={theme}
              >
                <Download size={14} />
                CSV
              </button>
              <button
                onClick={exportJPG}
                className="
                flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors duration-200
                data-[theme=light]:bg-slate-100 data-[theme=light]:text-slate-700 data-[theme=light]:hover:bg-slate-200
                data-[theme=dark]:bg-slate-800 data-[theme=dark]:text-slate-200 data-[theme=dark]:hover:bg-slate-700
              "
                data-theme={theme}
              >
                <Download size={14} />
                JPG
              </button>
              <button
                onClick={exportPortableHTML}
                className="
                flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors duration-200
                data-[theme=light]:bg-slate-100 data-[theme=light]:text-slate-700 data-[theme=light]:hover:bg-slate-200
                data-[theme=dark]:bg-slate-800 data-[theme=dark]:text-slate-200 data-[theme=dark]:hover:bg-slate-700
              "
                data-theme={theme}
                title="Export as Presentation Case Report"
              >
                <Share size={14} />
                Case Report
              </button>
              <button
                onClick={exportJSON}
                className="
                flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors duration-200
                data-[theme=light]:bg-slate-100 data-[theme=light]:text-slate-700 data-[theme=light]:hover:bg-slate-200
                data-[theme=dark]:bg-slate-800 data-[theme=dark]:text-slate-200 data-[theme=dark]:hover:bg-slate-700
              "
                data-theme={theme}
                title="Backup as JSON"
              >
                <Download size={14} />
                Export
              </button>
              <button
                onClick={importJSON}
                className="
                flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors duration-200
                data-[theme=light]:bg-slate-100 data-[theme=light]:text-slate-700 data-[theme=light]:hover:bg-slate-200
                data-[theme=dark]:bg-slate-800 data-[theme=dark]:text-slate-200 data-[theme=dark]:hover:bg-slate-700
              "
                data-theme={theme}
              >
                <Upload size={14} />
                Import
              </button>
            </div>

            {/* Mobile "More" Menu - Simple implementation using a relative container for now */}
            <div className="md:hidden relative group">
              <button className="
              p-1.5 rounded transition-colors duration-200
              data-[theme=light]:bg-slate-100 data-[theme=light]:text-slate-700
              data-[theme=dark]:bg-slate-800 data-[theme=dark]:text-slate-200
            " data-theme={theme}>
                <MoreHorizontal size={16} />
              </button>
              <div className="
              absolute right-0 top-full mt-1 w-40 rounded-lg shadow-xl py-1 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200 z-50
              bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
            ">
                <button onClick={exportCSV} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
                  <Download size={14} /> Export CSV
                </button>
                <button onClick={exportJPG} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
                  <Download size={14} /> Export JPG
                </button>
                <button onClick={importJSON} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
                  <Upload size={14} /> Import JSON
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className={`
              flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors duration-200
              ${showSidebar
                  ? 'bg-blue-600 text-white'
                  : 'data-[theme=light]:bg-slate-100 data-[theme=light]:text-slate-700 data-[theme=light]:hover:bg-slate-200 data-[theme=dark]:bg-slate-800 data-[theme=dark]:text-slate-200 data-[theme=dark]:hover:bg-slate-700'
                }
            `}
              data-theme={theme}
              title="Properties"
            >
              <Settings size={14} />
              <span className="hidden sm:inline">Properties</span>
            </button>
            <button
              onClick={() => setShowRecovery(true)}
              className="
              flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors duration-200
              data-[theme=light]:bg-amber-100 data-[theme=light]:text-amber-700 data-[theme=light]:hover:bg-amber-200
              data-[theme=dark]:bg-amber-900/30 data-[theme=dark]:text-amber-200 data-[theme=dark]:hover:bg-amber-900/50
            "
              data-theme={theme}
              title="Rescue Center"
            >
              <Shield size={14} />
              <span className="hidden sm:inline text-amber-600 dark:text-amber-400">Rescue</span>
            </button>
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="
                  flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold transition-colors duration-200
                  bg-purple-100 text-purple-700 hover:bg-purple-200
                  dark:bg-purple-900/30 dark:text-purple-200 dark:hover:bg-purple-900/50
                "
              >
                <Shield size={14} />
                Admin
              </button>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="
              flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors duration-200
              data-[theme=light]:bg-slate-100 data-[theme=light]:text-slate-700 data-[theme=light]:hover:bg-slate-200
              data-[theme=dark]:bg-slate-800 data-[theme=dark]:text-slate-200 data-[theme=dark]:hover:bg-slate-700
            "
              data-theme={theme}
            >
              <User size={14} />
              Account
            </button>
          </div>
        </div>

        {/* Context Menu */}
        {
          contextMenu && (
            <ContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              canvasPosition={contextMenu.canvasPosition}
              onClose={() => setContextMenu(null)}
              onAddNode={addNodeAtPosition}
              onDuplicateSelected={selectedNodeId || selectedEdgeId ? duplicateSelected : undefined}
              onDeleteSelected={selectedNodeId || selectedEdgeId ? deleteSelected : undefined}
              onApplyLayout={applyAutoLayout}
              hasSelection={!!selectedNodeId || !!selectedEdgeId}
            />
          )
        }

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Canvas Wrapper */}
          <div className="flex-1 relative overflow-hidden">
            <div id="reactflow-wrapper" className="h-full">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={handleNodeClick}
                onEdgeClick={handleEdgeClick}
                onPaneClick={handlePaneClick}
                onPaneContextMenu={handlePaneContextMenu}
                onNodeDragStart={handleNodeDragStart}
                onNodeDragStop={handleNodeDragStop}
                nodeTypes={nodeTypes}
                panOnDrag={selectedTool === 'boxSelect' ? [2] : true}
                selectionOnDrag={selectedTool === 'boxSelect'}
                panOnScroll={false}
                selectionMode={SelectionMode.Partial}
                multiSelectionKeyCode="Shift"
                snapToGrid={false}
                fitView
                fitViewOptions={{ padding: 0.2, maxZoom: 1.0, minZoom: 0.1 }}
                minZoom={0.1}
                maxZoom={2}
                className="
                transition-colors duration-200
                data-[theme=light]:bg-gray-100
                data-[theme=dark]:bg-gray-900
              "
                style={{
                  cursor: selectedTool === 'boxSelect' ? 'crosshair' : 'default'
                }}
                nodesDraggable={true}
                nodesConnectable={true}
                elementsSelectable={true}
                proOptions={{ hideAttribution: true }}
              >
                <Background
                  variant={BackgroundVariant.Dots}
                  gap={20}
                  size={1}
                  color={theme === 'light' ? '#D1D5DB' : '#4B5563'}
                />
                <MiniMap
                  style={{ bottom: 40 }}
                  className={`
                  border transform scale-75 origin-bottom-right transition-colors duration-200
                  ${theme === 'light'
                      ? 'bg-white border-gray-300'
                      : 'bg-gray-800 border-gray-700'
                    }
                `}
                  nodeColor={(node) => node.data.color}
                  maskColor={theme === 'light' ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.2)"}
                />
              </ReactFlow>
            </div>

            {/* Custom Bottom Controls (Zoom + Undo/Redo) - Positioned to the left of the Minimap */}
            <div className="absolute bottom-[45px] right-[180px] flex items-center gap-2 z-10 transition-all duration-300">
              {/* Undo/Redo Group */}
              <div className="flex rounded-md shadow-sm overflow-hidden border border-gray-200 dark:border-slate-700">
                <button
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  className="
                  w-9 h-9 flex items-center justify-center transition-all duration-200
                  bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed
                  dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700 border-r border-gray-200 dark:border-slate-700
                "
                  title="Undo (Ctrl+Z)"
                >
                  <Undo size={16} />
                </button>
                <button
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                  className="
                  w-9 h-9 flex items-center justify-center transition-all duration-200
                  bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed
                  dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700
                "
                  title="Redo (Ctrl+Y)"
                >
                  <Redo size={16} />
                </button>
              </div>

              <div className="w-px h-6 bg-gray-300 dark:bg-slate-600 mx-1" />

              {/* Zoom Controls */}
              <div className="flex rounded-md shadow-sm overflow-hidden border border-gray-200 dark:border-slate-700">
                <button
                  onClick={handleFitView}
                  className="
                  w-9 h-9 flex items-center justify-center transition-all duration-200
                  bg-white text-gray-700 hover:bg-gray-50
                  dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700 border-r border-gray-200 dark:border-slate-700
                "
                  title="Fit View"
                >
                  <Maximize2 size={16} />
                </button>
                <button
                  onClick={handleZoomIn}
                  className="
                  w-9 h-9 flex items-center justify-center transition-all duration-200
                  bg-white text-gray-700 hover:bg-gray-50
                  dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700 border-r border-gray-200 dark:border-slate-700
                "
                  title="Zoom In"
                >
                  <Plus size={16} />
                </button>
                <button
                  onClick={handleZoomOut}
                  className="
                  w-9 h-9 flex items-center justify-center transition-all duration-200
                  bg-white text-gray-700 hover:bg-gray-50
                  dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700
                "
                  title="Zoom Out"
                >
                  <Minus size={16} />
                </button>
              </div>
            </div>

            {/* Toolbar */}
            <Toolbar
              selectedTool={selectedTool}
              snapToGrid={snapToGrid}
              theme={theme}
              onToolChange={setSelectedTool}
              onToggleSnap={() => setSnapToGrid(!snapToGrid)}
              onAddNode={addNode}
              onDeleteSelected={deleteSelected}
              onDuplicateSelected={duplicateSelected}
              onApplyLayout={applyAutoLayout}
              onFitView={handleFitView}
              onAddStickyNote={addStickyNote}
              hasSelection={!!(selectedNodeId || selectedEdgeId)}
            />

            {/* Transparent Ruler Overlay — sits at the bottom of the canvas, no background */}
            <div className="absolute inset-x-0 bottom-0 z-[5] pointer-events-none h-32 flex items-end">
              <Timeline
                nodes={nodes.map((n: any) => n.data)}
                edges={edges.map((e: any) => ({
                  id: e.id,
                  source: e.source,
                  target: e.target,
                  label: (e.label as string) || '',
                  color: (e.style?.stroke as string) || '#6B7280',
                  ts: e.data?.ts,
                  notesMd: e.data?.notesMd || ''
                }))}
                onSelectNode={handleSelectNodeFromTimeline}
                selectedNodeId={selectedNodeId}
              />
            </div>
          </div>

          {showSidebar && (
            <div className="absolute top-16 right-4 z-20 w-84 h-[calc(100vh-8rem)] flex flex-col pointer-events-none">
              <div className="pointer-events-auto h-full overflow-hidden bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-lg shadow-2xl border border-gray-200 dark:border-slate-700 transition-all duration-300">
                <Sidebar
                  selectedNode={selectedNode}
                  selectedEdge={selectedBoardEdge}
                  theme={theme}
                  onUpdateNode={updateNode}
                  onUpdateEdge={updateEdge}
                  onClose={() => {
                    setSelectedNodeId(null);
                    setSelectedEdgeId(null);
                    setShowSidebar(false);
                  }}
                />
              </div>
            </div>
          )}
        </div>




        {/* Modals */}
        <BoardLibrary
          isOpen={showLibrary}
          onClose={() => setShowLibrary(false)}
          onSelectBoard={loadBoard}
          onCreateBoard={createNewBoard}
          currentBoardId={currentBoard?.id}
        />

        <RecoveryUtility
          isOpen={showRecovery}
          onClose={() => setShowRecovery(false)}
        />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileImport}
          className="hidden"
        />
      </div>
    </BoardProvider >
  );
};

const App: React.FC = () => {
  return (
    <ReactFlowProvider>
      <AppContent />
    </ReactFlowProvider>
  );
};

export default App;
